import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { CalendarToday as CalendarIcon, Schedule as DurationIcon } from '@mui/icons-material';
import { formatDateForInput } from '../utils/dateUtils';

const DurationSelector = ({ 
  startDate, 
  endDate, 
  onEndDateChange, 
  label = "End Date",
  required = false,
  helperText = "",
  defaultDuration = null, // e.g., { value: 1, unit: 'years' }
  minDate = null // e.g., today's date in YYYY-MM-DD format
}) => {
  const [mode, setMode] = useState('date'); // 'date' or 'duration'
  const [duration, setDuration] = useState({ value: '', unit: 'weeks' });
  const [hasCalculatedFromDates, setHasCalculatedFromDates] = useState(false);
  const [hasAppliedDefault, setHasAppliedDefault] = useState(false);

  // Apply default duration when startDate is set and no endDate exists
  useEffect(() => {
    if (defaultDuration && startDate && !endDate && !hasAppliedDefault) {
      const calculatedEndDate = calculateEndDate(defaultDuration.value, defaultDuration.unit, startDate);
      onEndDateChange(calculatedEndDate);
      setHasAppliedDefault(true);
    }
  }, [startDate, endDate, defaultDuration, hasAppliedDefault]);

  // Calculate duration when switching to duration mode (only once)
  useEffect(() => {
    
    if (mode === 'duration' && startDate && endDate && !hasCalculatedFromDates) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Simplified logic: default to weeks for most cases
      if (diffDays >= 365 && diffDays % 365 === 0) {
        setDuration({ value: Math.floor(diffDays / 365), unit: 'years' });
      } else if (diffDays >= 28 && diffDays % 7 === 0) {
        // If it's divisible by 7 and at least 4 weeks, use weeks
        setDuration({ value: Math.floor(diffDays / 7), unit: 'weeks' });
      } else if (diffDays >= 28) {
        // For longer periods that aren't exact weeks, use months
        setDuration({ value: Math.round(diffDays / 30), unit: 'months' });
      } else {
        // For shorter periods, always use weeks (minimum 1)
        setDuration({ value: Math.max(1, Math.round(diffDays / 7)), unit: 'weeks' });
      }
      setHasCalculatedFromDates(true);
    }
  }, [mode, startDate, endDate, hasCalculatedFromDates]);

  // Reset calculation flag when dates change externally
  useEffect(() => {
    if (mode === 'date') {
      setHasCalculatedFromDates(false);
    }
  }, [startDate, endDate, mode]);

  // Calculate end date based on duration
  const calculateEndDate = (durationValue, unit, startDateValue) => {
    if (!startDateValue || !durationValue) return '';
    
    const start = new Date(startDateValue);
    const value = parseInt(durationValue);
    
    // Create a copy to avoid mutating the original date
    const endDate = new Date(start);
    
    switch (unit) {
      case 'weeks':
        endDate.setDate(endDate.getDate() + (value * 7));
        break;
      case 'months':
        endDate.setMonth(endDate.getMonth() + value);
        break;
      case 'years':
        endDate.setFullYear(endDate.getFullYear() + value);
        break;
      default:
        console.warn('Unknown unit:', unit);
        break;
    }
    const formatted = formatDateForInput(endDate);
    
    return formatted;
  };

  const handleModeToggle = () => {
    const newMode = mode === 'date' ? 'duration' : 'date';
    setMode(newMode);
    
    // Reset the calculation flag when switching modes
    if (newMode === 'date') {
      setHasCalculatedFromDates(false);
    }
  };

  const handleDurationChange = (field, value) => {
    const newDuration = { ...duration, [field]: value };
    setDuration(newDuration);
    
    if (startDate && newDuration.value) {
      const calculatedEndDate = calculateEndDate(newDuration.value, newDuration.unit, startDate);
      onEndDateChange(calculatedEndDate);
    }
  };

  const handleDirectDateChange = (value) => {
    onEndDateChange(value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'date' ? (
          <TextField
            fullWidth
            label={label}
            type="date"
            value={endDate || ''}
            onChange={(e) => handleDirectDateChange(e.target.value)}
            required={required}
            InputLabelProps={{ shrink: true }}
            helperText={helperText}
            inputProps={minDate ? { min: minDate } : undefined}
          />
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration"
                type="number"
                value={duration.value}
                onChange={(e) => handleDurationChange('value', e.target.value)}
                required={required}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={duration.unit}
                  onChange={(e) => handleDurationChange('unit', e.target.value)}
                  label="Unit"
                >
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
        
        <Button
          variant="text"
          size="small"
          onClick={handleModeToggle}
          sx={{ 
            minWidth: 'auto', 
            px: 1, 
            py: 0.25, 
            fontSize: '0.75rem',
            textTransform: 'none'
          }}
          startIcon={mode === 'date' ? <DurationIcon /> : <CalendarIcon />}
        >
          {mode === 'date' ? 'Duration' : 'Date'}
        </Button>
      </Box>
      
      {mode === 'duration' && endDate && (
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
          End Date: {new Date(endDate).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </Box>
      )}
    </Box>
  );
};

export default DurationSelector; 